import { useMemo, useRef, useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { FluentEmoji } from '@/components/ui/fluent-emoji'

interface ReminderTimePickerProps {
  value: string
  onChange: (time: string) => void
  disabled?: boolean
  ariaLabel?: string
}

function to12HourParts(time: string): { hour12: number; minute: number; period: 'AM' | 'PM' } {
  const [hours, minutes] = time.split(':').map(Number)
  const safeHours = Number.isNaN(hours) ? 9 : hours
  const safeMinutes = Number.isNaN(minutes) ? 0 : minutes
  const period: 'AM' | 'PM' = safeHours >= 12 ? 'PM' : 'AM'
  const hour12 = safeHours % 12 || 12
  return { hour12, minute: safeMinutes, period }
}

function to24HourString(hour12: number, minute: number, period: 'AM' | 'PM'): string {
  let hour24 = hour12 % 12
  if (period === 'PM') hour24 += 12
  return `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
}

function formatReminderTime(time: string): string {
  const [hours, minutes] = time.split(':').map(Number)
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return time
  const date = new Date()
  date.setHours(hours, minutes, 0, 0)
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

export function ReminderTimePicker({
  value,
  onChange,
  disabled = false,
  ariaLabel = 'Reminder time',
}: ReminderTimePickerProps) {
  const nativeInputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)

  const isWindows = useMemo(
    () => typeof window !== 'undefined' && /Windows/i.test(window.navigator.userAgent),
    [],
  )

  const { hour12, minute, period } = useMemo(() => to12HourParts(value), [value])

  const triggerClassName =
    'flex min-w-[132px] items-center justify-between gap-2 rounded-xl border border-indigo-300/70 bg-white px-3 py-2 text-slate-900 shadow-sm transition-colors dark:border-indigo-500/40 dark:bg-slate-900 dark:text-slate-100 disabled:cursor-not-allowed disabled:opacity-60'

  if (!isWindows) {
    return (
      <div className="relative">
        <input
          ref={nativeInputRef}
          type="time"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="sr-only"
          aria-label={ariaLabel}
          disabled={disabled}
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            nativeInputRef.current?.showPicker?.()
            nativeInputRef.current?.focus()
            nativeInputRef.current?.click()
          }}
          className={triggerClassName}
        >
          <span className="text-sm font-semibold tabular-nums">{formatReminderTime(value)}</span>
          <FluentEmoji emoji="🕒" size={14} />
        </button>
      </div>
    )
  }

  const updateParts = (next: Partial<{ hour12: number; minute: number; period: 'AM' | 'PM' }>) => {
    const h = next.hour12 ?? hour12
    const m = next.minute ?? minute
    const p = next.period ?? period
    onChange(to24HourString(h, m, p))
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} className={triggerClassName}>
          <span className="text-sm font-semibold tabular-nums">{formatReminderTime(value)}</span>
          <FluentEmoji emoji="🕒" size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] border-indigo-200/60 bg-white/98 p-3 dark:border-indigo-700/50 dark:bg-slate-900/98">
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs text-slate-500">
            Hour
            <select
              value={hour12}
              onChange={(e) => updateParts({ hour12: Number(e.target.value) })}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                <option key={h} value={h}>
                  {h.toString().padStart(2, '0')}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-500">
            Minute
            <select
              value={minute}
              onChange={(e) => updateParts({ minute: Number(e.target.value) })}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            >
              {Array.from({ length: 60 }, (_, i) => i).map((m) => (
                <option key={m} value={m}>
                  {m.toString().padStart(2, '0')}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {(['AM', 'PM'] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => updateParts({ period: p })}
              className={`rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                period === p
                  ? 'border-indigo-500 bg-indigo-500 text-white'
                  : 'border-slate-300 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
